import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import { usePermissions } from '../../utils/usePermissions';
import HistoriquePrixModal from '../fournisseurs/HistoriquePrixModal';
import './ProduitDetail.css';

const API_URL = import.meta.env.VITE_API_URL;

const hasValidDataSheet = (dataSheetPath) => {
    if (typeof dataSheetPath !== 'string') return false;
    const normalizedPath = dataSheetPath.trim();
    if (!normalizedPath) return false;

    const loweredPath = normalizedPath.toLowerCase();
    return loweredPath !== 'null' && loweredPath !== 'undefined';
};

const createDefaultDecoupeLineaire = () => ({
    nombrePieces: 1,
    longueurPieceMetre: 1,
    metres: 1
});

const normalizeDecoupeLineaire = (decoupe) => {
    const source = decoupe && typeof decoupe === 'object' ? decoupe : {};

    const nombrePieces = Math.max(1, parseInt(source.nombrePieces, 10) || 1);
    const longueurPieceMetre = Math.max(0.001, Number(source.longueurPieceMetre) || 0.001);
    const metres = Number((nombrePieces * longueurPieceMetre).toFixed(3));

    return {
        nombrePieces,
        longueurPieceMetre,
        metres
    };
};

const normalizeDecoupesLineaires = (decoupes) => {
    if (!Array.isArray(decoupes)) return [];
    return decoupes.map((decoupe) => normalizeDecoupeLineaire(decoupe));
};

const calculateTotalMetres = (decoupes) => {
    if (!Array.isArray(decoupes) || decoupes.length === 0) return 0;
    return Number(decoupes.reduce((sum, decoupe) => {
        return sum + (Number(decoupe.metres) || 0);
    }, 0).toFixed(3));
};

const normalizeDecoupeForDisplay = (decoupe) => {
    const nombrePieces = Math.max(1, parseInt(decoupe && decoupe.nombrePieces, 10) || 1);
    const longueurPieceMetre = Math.max(0.001, Number(decoupe && decoupe.longueurPieceMetre) || 0.001);
    const metres = Number(decoupe && decoupe.metres) || Number((nombrePieces * longueurPieceMetre).toFixed(3));
    return { nombrePieces, longueurPieceMetre, metres };
};

const formatQuantiteAffichage = (produit) => {
    if (!produit) return '0';
    if (produit.modeGestionStock !== 'lineaire') {
        return `${Math.max(0, (Number(produit.quantite) || 0) - (Number(produit.quantiteReservee) || 0))}`;
    }

    const decoupes = Array.isArray(produit.decoupesLineaires)
        ? produit.decoupesLineaires.map(normalizeDecoupeForDisplay).filter(d => d.metres > 0)
        : [];

    if (decoupes.length === 1) {
        const { nombrePieces, longueurPieceMetre } = decoupes[0];
        return `${nombrePieces} piece${nombrePieces > 1 ? 's' : ''} x ${longueurPieceMetre} m`;
    }

    if (decoupes.length > 1) {
        return decoupes
            .map((decoupe) => `${decoupe.nombrePieces} piece${decoupe.nombrePieces > 1 ? 's' : ''} x ${decoupe.longueurPieceMetre} m`)
            .join(' + ');
    }

    return `${Number(produit.quantite) || 0} m`;
};

const getStockCounters = (produit) => {
    const physique = Math.max(0, Number(produit?.quantite) || 0);
    const reserve = Math.max(0, Number(produit?.quantiteReservee) || 0);
    const vendu = Math.max(0, Number(produit?.quantiteVendue) || 0);
    return {
        disponible: Math.max(0, physique - reserve),
        reserve,
        vendu
    };
};

const ProduitDetail = ({ onNavigate, onLogout, currentPage, produitId, onBack }) => {
    const { hasPermission } = usePermissions();
    const [produit, setProduit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newQuantite, setNewQuantite] = useState(0);
    const [newModeGestionStock, setNewModeGestionStock] = useState('unite');
    const [newPrix, setNewPrix] = useState(0);
    const [newPrixAchat, setNewPrixAchat] = useState(0);
    const [newPrixMetre, setNewPrixMetre] = useState(0);
    const [newTVA, setNewTVA] = useState(19);
    const [newTauxGain, setNewTauxGain] = useState(0);
    const [newDecoupesLineaires, setNewDecoupesLineaires] = useState([createDefaultDecoupeLineaire()]);
    const [newDataSheet, setNewDataSheet] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [deletingDataSheet, setDeletingDataSheet] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    useEffect(() => {
        fetchProduitDetails();
    }, [produitId]);

    const fetchProduitDetails = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/produits/${produitId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const produitData = response.data || {};
            const modeStock = produitData.modeGestionStock === 'lineaire' ? 'lineaire' : 'unite';
            const decoupesNormalisees = normalizeDecoupesLineaires(produitData.decoupesLineaires);
            const totalMetres = calculateTotalMetres(decoupesNormalisees);

            setProduit(produitData);
            setNewModeGestionStock(modeStock);

            if (modeStock === 'lineaire') {
                if (decoupesNormalisees.length > 0) {
                    setNewDecoupesLineaires(decoupesNormalisees);
                    setNewQuantite(totalMetres);
                } else {
                    setNewDecoupesLineaires([createDefaultDecoupeLineaire()]);
                    setNewQuantite(Number(produitData.quantite) || 0);
                }
            } else {
                setNewDecoupesLineaires([createDefaultDecoupeLineaire()]);
                setNewQuantite(Number(produitData.quantite) || 0);
            }

            setNewPrix(Number(produitData.prix) || 0);
            setNewPrixAchat(Number(produitData.prixAchat) || 0);
            setNewPrixMetre(Number(produitData.prixMetre) || 0);
            setNewTVA(Number(produitData.TVA) || 19);
            setNewTauxGain(Number(produitData.tauxGain) || 0);
        } catch (error) {
            Swal.fire('Erreur', 'Impossible de charger les détails du produit', 'error');
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const syncDecoupesLineaires = (nextDecoupes) => {
        const decoupesNormalisees = normalizeDecoupesLineaires(nextDecoupes);
        const decoupesFinales = decoupesNormalisees.length > 0
            ? decoupesNormalisees
            : [createDefaultDecoupeLineaire()];

        setNewDecoupesLineaires(decoupesFinales);
        setNewQuantite(calculateTotalMetres(decoupesFinales));
    };

    const handleChangeDecoupeLineaire = (index, field, value) => {
        const nextDecoupes = newDecoupesLineaires.map((decoupe, idx) => {
            if (idx !== index) return decoupe;
            return { ...decoupe, [field]: value };
        });

        syncDecoupesLineaires(nextDecoupes);
    };

    const handleAddDecoupeLineaire = () => {
        syncDecoupesLineaires([...newDecoupesLineaires, createDefaultDecoupeLineaire()]);
    };

    const handleRemoveDecoupeLineaire = (index) => {
        if (newDecoupesLineaires.length <= 1) {
            syncDecoupesLineaires([createDefaultDecoupeLineaire()]);
            return;
        }

        const nextDecoupes = newDecoupesLineaires.filter((_, idx) => idx !== index);
        syncDecoupesLineaires(nextDecoupes);
    };

    const handleUpdateStock = async (e) => {
        e.preventDefault();
        setUpdating(true);
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            const decoupesNormalisees = normalizeDecoupesLineaires(newDecoupesLineaires);
            const totalMetres = calculateTotalMetres(decoupesNormalisees);

            if (newModeGestionStock === 'lineaire') {
                formData.append('quantite', totalMetres);
                formData.append('decoupesLineaires', JSON.stringify(decoupesNormalisees));
            } else {
                formData.append('quantite', newQuantite);
                formData.append('decoupesLineaires', '[]');
            }

            formData.append('modeGestionStock', newModeGestionStock);
            formData.append('prix', newPrix);
            formData.append('prixAchat', newPrixAchat);
            formData.append('prixMetre', newModeGestionStock === 'lineaire' ? newPrixMetre : 0);
            formData.append('TVA', newTVA);
            formData.append('tauxGain', newTauxGain);
            if (newDataSheet) formData.append('dataSheet', newDataSheet);

            await axios.put(`${API_URL}/produits/${produitId}`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            Swal.fire({
                title: 'Succès !',
                text: 'Les informations ont été mises à jour.',
                icon: 'success',
                confirmButtonColor: '#1e3a5f'
            });
            fetchProduitDetails();
            setNewDataSheet(null);
        } catch (error) {
            Swal.fire('Erreur', error.response?.data?.message || 'Erreur lors de la mise à jour', 'error');
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteDataSheet = async () => {
        if (!produitId) return;

        const result = await Swal.fire({
            title: 'Supprimer la fiche technique ?',
            text: 'Cette action supprimera aussi le fichier dans le dossier uploads.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer'
        });

        if (!result.isConfirmed) return;

        try {
            setDeletingDataSheet(true);
            const token = localStorage.getItem('token');

            await axios.delete(`${API_URL}/produits/${produitId}/datasheet`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire('Supprimée', 'La fiche technique a été supprimée.', 'success');
            fetchProduitDetails();
        } catch (error) {
            Swal.fire('Erreur', error.response?.data?.message || 'Impossible de supprimer la fiche technique', 'error');
        } finally {
            setDeletingDataSheet(false);
        }
    };

    if (loading) {
        return (
            <div className="pm-layout">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="pm-content">
                    <div className="pd-wrap">
                        <div className="pd-loading">
                            <i className="fas fa-circle-notch fa-spin fa-3x"></i>
                            <p>Chargement des détails...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!produit) {
        return (
            <div className="pm-layout">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="pm-content">
                    <div className="pd-wrap">
                        <div className="pd-error">Produit non trouvé</div>
                        <button className="pd-btn-back" onClick={onBack}>
                            <i className="fas fa-arrow-left"></i> Retour
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const canShowDataSheet = hasValidDataSheet(produit.dataSheet);
    const dataSheetPath = canShowDataSheet ? produit.dataSheet.trim() : '';

    return (
        <div className="pm-layout">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
            <div className="pm-content">
                <div className="pd-wrap">
                    {/* Header Moderne */}
                    <div className="pd-header">
                        <div className="pd-header-left">
                            <div className="pd-breadcrumb">Administration / Produits / Détails</div>
                            <h1>Fiche Produit</h1>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button className="pd-btn-back" style={{ background: '#ecfdf5', color: '#10b981', border: '1px solid #a7f3d0' }} onClick={() => setShowHistoryModal(true)}>
                                <i className="fas fa-chart-line"></i> Historique Prix Devis
                            </button>
                            <button className="pd-btn-back" onClick={onBack}>
                                <i className="fas fa-chevron-left"></i> Retour à la gestion
                            </button>
                        </div>
                    </div>

                    <div className="pd-single-column">
                        {/* Section Image & Meta en haut */}
                        <div className="pd-top-section">
                            <div className="pd-image-card">
                                <img
                                    src={`${API_URL.replace('/api', '')}${produit.imageProduit}`}
                                    alt={produit.nomProduit}
                                    className="pd-main-img"
                                />
                            </div>

                            <div className="pd-meta-grid-top">
                                {canShowDataSheet && (
                                    <div className="pd-meta-group">
                                        <span className="pd-meta-label">Documentation</span>
                                        <div className="pd-doc-actions">
                                            <a
                                                href={`${API_URL.replace('/api', '')}${dataSheetPath}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="pd-link-pdf"
                                            >
                                                <i className="fas fa-file-pdf"></i> Voir la fiche technique
                                            </a>
                                            {hasPermission('produits', 'delete') && (
                                                <button
                                                    type="button"
                                                    className="pd-btn-delete-datasheet"
                                                    onClick={handleDeleteDataSheet}
                                                    title="Supprimer la fiche technique"
                                                    disabled={deletingDataSheet}
                                                >
                                                    <i className={`fas ${deletingDataSheet ? 'fa-spinner fa-spin' : 'fa-trash-alt'}`}></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Reste du contenu en dessous */}
                        <div className="pd-bottom-content">
                            <div className="pd-info-card-main">
                                {/* Titre Section */}
                                <div className="pd-title-row">
                                    <h2>{produit.nomProduit}</h2>
                                </div>

                                {/* Stats Bar */}
                                <div className="pd-stats-bar">
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label">Prix Achat</span>
                                        <span className="pd-stat-value price" style={{color: '#dc3545'}}>{produit.prixAchat?.toLocaleString('fr-FR')} TND</span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label">Mode Stock</span>
                                        <span className="pd-stat-value">{produit.modeGestionStock === 'lineaire' ? 'Linéaire (m)' : 'Unité'}</span>
                                    </div>
                                    {produit.modeGestionStock === 'lineaire' && (
                                        <div className="pd-stat-item">
                                            <span className="pd-stat-label">Prix au mètre</span>
                                            <span className="pd-stat-value price" style={{color: '#0f766e'}}>{(produit.prixMetre || 0).toLocaleString('fr-FR')} TND/m</span>
                                        </div>
                                    )}
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label">TVA</span>
                                        <span className="pd-stat-value">{produit.TVA || 19} %</span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label">Taux Gain</span>
                                        <span className="pd-stat-value">{produit.tauxGain || 0} %</span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label">Prix Public</span>
                                        <span className="pd-stat-value price">{produit.prix?.toLocaleString('fr-FR')} TND</span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label">Stock Actuel (Dispo)</span>
                                        <span className="pd-stat-value stock">
                                            {formatQuantiteAffichage(produit)}
                                        </span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label" style={{color: '#d97706'}}>Stock réservé</span>
                                        <span className="pd-stat-value stock" style={{color: '#d97706'}}>
                                            {getStockCounters(produit).reserve}
                                        </span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label" style={{color: '#dc2626'}}>Stock Vendu</span>
                                        <span className="pd-stat-value stock" style={{color: '#dc2626'}}>
                                            {getStockCounters(produit).vendu}
                                        </span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label" style={{color: '#ef4444'}}>Stock Défectueux</span>
                                        <span className="pd-stat-value stock" style={{color: '#ef4444'}}>
                                            {produit.quantiteDefectueuse || 0}
                                        </span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label" style={{color: '#f59e0b'}}>Stock Perdu/Cassé</span>
                                        <span className="pd-stat-value stock" style={{color: '#f59e0b'}}>
                                            {produit.quantitePerdue || 0}
                                        </span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label" style={{color: '#3b82f6'}}>Stock Retourné</span>
                                        <span className="pd-stat-value stock" style={{color: '#3b82f6'}}>
                                            {produit.quantiteRetournee || 0}
                                        </span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label">Date Création</span>
                                        <span className="pd-stat-value">{new Date(produit.createdAt).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                    <div className="pd-stat-item">
                                        <span className="pd-stat-label">Mise à jour</span>
                                        <span className="pd-stat-value">{new Date(produit.updatedAt).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                </div>

                                {produit.modeGestionStock === 'lineaire' && Array.isArray(produit.decoupesLineaires) && produit.decoupesLineaires.length > 0 && (
                                    <div className="pd-linear-breakdown">
                                        <h4><i className="fas fa-ruler-combined"></i> Détail des découpes en stock</h4>
                                        <div className="pd-linear-breakdown-list">
                                            {produit.decoupesLineaires.map((decoupe, index) => {
                                                const nombrePieces = Math.max(1, parseInt(decoupe && decoupe.nombrePieces, 10) || 1);
                                                const longueurPieceMetre = Math.max(0.001, Number(decoupe && decoupe.longueurPieceMetre) || 0.001);
                                                const metres = Number((nombrePieces * longueurPieceMetre).toFixed(3));

                                                return (
                                                    <div key={`decoupe-stock-${index}`} className="pd-linear-breakdown-item">
                                                        <span className="pd-linear-breakdown-index">#{index + 1}</span>
                                                        <span className="pd-linear-breakdown-text">
                                                            {nombrePieces} pièce{nombrePieces > 1 ? 's' : ''} x {longueurPieceMetre} m = {metres} m
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Stock Management Section */}
                                <div className="pd-stock-section">
                                    <div className="pd-stock-title-row">
                                        <h4><i className="fas fa-sliders-h"></i> Paramètres de Vente</h4>
                                        <p className="pd-stock-subtitle">Ajustez le prix, le stock et les documents techniques</p>
                                    </div>

                                    <form onSubmit={handleUpdateStock} className="pd-stock-form">
                                        <div className="pd-stock-grid">
                                            <div className="pd-stock-field">
                                                <label><i className="fas fa-sliders-h"></i> Mode de stock</label>
                                                <div className="pd-input-wrapper">
                                                    <select
                                                        value={newModeGestionStock}
                                                        onChange={(e) => {
                                                            const selectedMode = e.target.value;
                                                            setNewModeGestionStock(selectedMode);

                                                            if (selectedMode !== 'lineaire') {
                                                                setNewPrixMetre(0);
                                                            } else {
                                                                const decoupesExistantes = normalizeDecoupesLineaires(newDecoupesLineaires);
                                                                const decoupesInitiales = decoupesExistantes.length > 0
                                                                    ? decoupesExistantes
                                                                    : [createDefaultDecoupeLineaire()];
                                                                setNewDecoupesLineaires(decoupesInitiales);
                                                                setNewQuantite(calculateTotalMetres(decoupesInitiales));
                                                            }
                                                        }}
                                                        style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none' }}
                                                    >
                                                        <option value="unite">Unité (pièce)</option>
                                                        <option value="lineaire">Linéaire (mètre)</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="pd-stock-field">
                                                <label><i className="fas fa-box-open"></i> {newModeGestionStock === 'lineaire' ? 'Stock (m)' : 'Quantité'}</label>
                                                <div className="pd-input-wrapper">
                                                    <input
                                                        type="number"
                                                        value={newQuantite}
                                                        onChange={(e) => {
                                                            if (newModeGestionStock !== 'lineaire') {
                                                                setNewQuantite(e.target.value);
                                                            }
                                                        }}
                                                        min="0"
                                                        step={newModeGestionStock === 'lineaire' ? '0.001' : '1'}
                                                        readOnly={newModeGestionStock === 'lineaire'}
                                                        required
                                                    />
                                                    <span className="pd-input-unit">{newModeGestionStock === 'lineaire' ? 'M' : 'UNITÉS'}</span>
                                                </div>
                                            </div>
                                            {newModeGestionStock === 'lineaire' && (
                                                <div className="pd-stock-field pd-stock-field-decoupes">
                                                    <label><i className="fas fa-ruler-combined"></i> Découpes (plusieurs longueurs dans le même produit)</label>
                                                    <div className="pd-decoupes-list">
                                                        {newDecoupesLineaires.map((decoupe, index) => (
                                                            <div key={`decoupe-edit-${index}`} className="pd-decoupe-row">
                                                                <div className="pd-decoupe-input-group">
                                                                    <span>Pièces</span>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        step="1"
                                                                        value={decoupe.nombrePieces}
                                                                        onChange={(e) => handleChangeDecoupeLineaire(index, 'nombrePieces', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="pd-decoupe-input-group">
                                                                    <span>Longueur (m)</span>
                                                                    <input
                                                                        type="number"
                                                                        min="0.001"
                                                                        step="0.001"
                                                                        value={decoupe.longueurPieceMetre}
                                                                        onChange={(e) => handleChangeDecoupeLineaire(index, 'longueurPieceMetre', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="pd-decoupe-total">
                                                                    {(Number(decoupe.metres) || 0).toFixed(3)} m
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="pd-btn-remove-decoupe"
                                                                    onClick={() => handleRemoveDecoupeLineaire(index)}
                                                                    disabled={newDecoupesLineaires.length === 1}
                                                                    title="Supprimer cette découpe"
                                                                >
                                                                    <i className="fas fa-trash-alt"></i>
                                                                </button>
                                                            </div>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            className="pd-btn-add-decoupe"
                                                            onClick={handleAddDecoupeLineaire}
                                                        >
                                                            <i className="fas fa-plus"></i> Ajouter une découpe
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="pd-stock-field">
                                                <label><i className="fas fa-hand-holding-usd"></i> Prix Achat</label>
                                                <div className="pd-input-wrapper">
                                                    <input
                                                        type="number"
                                                        value={newPrixAchat}
                                                        onChange={(e) => setNewPrixAchat(e.target.value)}
                                                        min="0"
                                                        step="0.001"
                                                        required
                                                    />
                                                    <span className="pd-input-unit">TND</span>
                                                </div>
                                            </div>
                                            <div className="pd-stock-field">
                                                <label><i className="fas fa-hand-holding-usd"></i> Prix Public</label>
                                                <div className="pd-input-wrapper">
                                                    <input
                                                        type="number"
                                                        value={newPrix}
                                                        onChange={(e) => setNewPrix(e.target.value)}
                                                        min="0"
                                                        step="0.001"
                                                        required
                                                    />
                                                    <span className="pd-input-unit">TND</span>
                                                </div>
                                            </div>
                                            <div className="pd-stock-field">
                                                <label><i className="fas fa-ruler-horizontal"></i> Prix au mètre</label>
                                                <div className="pd-input-wrapper">
                                                    <input
                                                        type="number"
                                                        value={newPrixMetre}
                                                        onChange={(e) => setNewPrixMetre(e.target.value)}
                                                        min="0"
                                                        step="0.001"
                                                        disabled={newModeGestionStock !== 'lineaire'}
                                                    />
                                                    <span className="pd-input-unit">TND/M</span>
                                                </div>
                                            </div>
                                            <div className="pd-stock-field">
                                                <label><i className="fas fa-percent"></i> TVA</label>
                                                <div className="pd-input-wrapper">
                                                    <input
                                                        type="number"
                                                        value={newTVA}
                                                        onChange={(e) => setNewTVA(e.target.value)}
                                                        min="0"
                                                        step="0.001"
                                                        required
                                                    />
                                                    <span className="pd-input-unit">%</span>
                                                </div>
                                            </div>
                                            <div className="pd-stock-field">
                                                <label><i className="fas fa-chart-line"></i> Taux Gain</label>
                                                <div className="pd-input-wrapper">
                                                    <input
                                                        type="number"
                                                        value={newTauxGain}
                                                        onChange={(e) => setNewTauxGain(e.target.value)}
                                                        min="0"
                                                        step="0.001"
                                                        required
                                                    />
                                                    <span className="pd-input-unit">%</span>
                                                </div>
                                            </div>
                                            <div className="pd-stock-field ">
                                                <label><i className="fas fa-file-upload"></i> Nouvelle Fiche Technique (PDF)</label>
                                                <div className="pd-file-input">
                                                    <input
                                                        type="file"
                                                        id="datasheet-upload-new"
                                                        accept=".pdf"
                                                        onChange={(e) => setNewDataSheet(e.target.files[0])}
                                                        className="pd-hidden-file-input"
                                                    />
                                                    <label htmlFor="datasheet-upload-new" className="pd-file-label">
                                                        <i className="fas fa-cloud-upload-alt fa-lg"></i>
                                                        {newDataSheet ? newDataSheet.name : "Cliquez pour uploader un nouveau PDF"}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <button type="submit" className="pd-btn-update-premium" disabled={updating}>
                                            {updating ? (
                                                <><i className="fas fa-spinner fa-spin"></i> Mise à jour en cours...</>
                                            ) : (
                                                <><i className="fas fa-check-circle"></i> Enregistrer les changements</>
                                            )}
                                        </button>
                                    </form>
                                </div>

                                {/* Description */}
                                <div className="pd-content-section">
                                    <h3>Description du produit</h3>
                                    <p className="pd-desc-text">{produit.description}</p>
                                </div>

                                {/* À propos de la marque */}
                                {produit.marque?.description && (
                                    <div className="pd-content-section">
                                        <h3>À propos de {produit.marque.name}</h3>
                                        <p className="pd-desc-text">{produit.marque.description}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <HistoriquePrixModal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                produitId={produitId}
                title={`Historique des prix devis - ${produit.nomProduit || ''}`}
            />
        </div>
    );
};

export default ProduitDetail;
